/**
 * Gespreksverslag-engine (migratie 176): één verslag voor alle disciplines.
 *
 *   GET  /v1/professional/gespreksverslag/context   sjabloon A/B/C, codes, lopende cases, cadans, plafond
 *   POST /v1/professional/gespreksverslag           route 1/2 (afspraak_id) of route 3 (adhoc + SMS)
 *   GET  /medewerker/gespreksverslagen               Vak A + Deel 5 met toestemmingsstatus
 *   GET  /employer/gespreksverslag-engine/...        B2B-updates (na toestemming) en verlengingsverzoeken
 */
import api from './api';
import type { CodeLabel } from './caseRegistratieApi';

const d = <T,>(r: { data: { data: T } }) => r.data.data;

export type Sjabloon = 'A' | 'B' | 'C';
export type VerlengingStatus = 'PENDING_HR_AUTHORIZATION' | 'AUTHORIZED_EXTENSION' | 'REJECTED' | null;

export interface LopendeCase {
  case_id: string;
  case_code: string;
  jaar: number;
  gestart_op: string;
  location_id: string | null;
  department_id: string | null;
  gesprekken: number;
}

export interface VerslagContext {
  route: 'ZELFBOEKING' | 'PROFESSIONAL_BOEKING' | 'AD_HOC';
  sjabloon: Sjabloon;
  sjabloon_label: string;
  employer_id: string;
  registratienummer: string | null;
  gesprek_datum: string;
  afspraak: { id: string; start_tijd: string; sessie_nummer: number } | null;
  bestaand_verslag_id: string | null;
  fte_toegestaan: boolean;
  codes: {
    categorieen: CodeLabel[];
    rolverhoudingen: CodeLabel[];
    rol_standaard: string | null;
    /** Alleen sjabloon A (H4.5, migratie 190). */
    geslachten_veroorzaker: CodeLabel[];
    interventies: CodeLabel[];
    doelen: CodeLabel[];
    arbo_kaders: CodeLabel[];
    fte_opties: number[];
    verzuim_opties: CodeLabel[];
    verlenging_opties: number[];
  };
  structuur: { locaties: Array<{ id: string; naam: string }>; afdelingen: Array<{ id: string; naam: string; location_id: string | null }> };
  lopende_cases: LopendeCase[];
  prefill: { is_new_incident: boolean; case_id: string | null; session_sequence_number: number; location_id: string | null; department_id: string | null };
  plafond: null | {
    max_sessies: number;
    goedgekeurd_extra: number;
    sessie_nummer: number;
    verlenging_status: VerlengingStatus;
    verlenging_venster: boolean;
    voorbij_plafond: boolean;
  };
  medewerker_portaal: boolean;
}

export interface VerslagInvoer {
  afspraak_id?: string;
  adhoc?: { employer_id: string; datum: string; tijd_minuten: number; employee_phone: string; toelichting?: string };
  is_new_incident: boolean;
  case_id?: string | null;
  session_sequence_number: number;
  location_id?: string | null;
  department_id?: string | null;
  categorieen?: string[];
  rolverhouding?: string | null;
  geslacht_veroorzaker?: string | null;
  interventies?: string[];
  doelen?: string[];
  client_facing_notes?: string | null;
  employer_facing_anonymized_summary?: string | null;
  deel5?: {
    arbo_preventive_purpose_enum: string;
    fte_belastbaarheid?: number | null;
    verwachte_verzuimreductie?: string | null;
    werkvloer_advies: string;
  } | null;
  verlenging_extra_sessies?: number | null;
}

export interface VerslagResultaat {
  verslag_id: string;
  sjabloon: Sjabloon;
  session_sequence_number: number;
  case_id: string | null;
  case_code: string | null;
  registratienummer: string | null;
  deel5_toestemming: 'sms' | 'portaal' | 'email' | 'geen_kanaal' | null;
  verlenging_status: VerlengingStatus;
  verificatie_status: 'PENDING_VERIFICATION' | 'VERIFIED' | 'REJECTED' | null;
  verificatie_verloopt_op: string | null;
}

export const getVerslagContext = (p: { afspraak_id: string } | { employer_id: string; datum: string }) =>
  api.get('/v1/professional/gespreksverslag/context', { params: p }).then(d<VerslagContext>);

export const dienVerslagIn = (invoer: VerslagInvoer) => api.post('/v1/professional/gespreksverslag', invoer).then(d<VerslagResultaat>);

/** Geen portaal of e-mailadres: de toestemmingsvraag voor Deel 5 per SMS (nummer wordt niet bewaard). */
export const stuurToestemmingSms = (verslagId: string, telefoon: string) =>
  api.post(`/v1/professional/gespreksverslag/${verslagId}/toestemming-sms`, { telefoon }).then(d<{ verstuurd_naar: string }>);

/** Cadans 1-3-5 — zelfde regel als de server (src/constants/gespreksverslag.ts). */
// ─── AI bij gespreksverslagen (alleen gepseudonimiseerde tekst naar de AI) ───

export interface AiInvoer {
  aantekeningen: string;
  sjabloon: Sjabloon;
  deel5: boolean;
  afdelingen?: string[];
  locaties?: string[];
  extra_termen?: string[];
}
export interface AiVoorbeeld {
  tekst: string;
  vervangingen: Array<{ plaatshouder: string; origineel: string }>;
}
export interface AiConcept {
  vak_a: string;
  vak_b: string;
  werkvloer_advies: string;
  vervangingen: number;
}

export const getAiStatus = () => api.get('/v1/professional/gespreksverslag/ai/status').then(d<{ beschikbaar: boolean }>);
export const getAiVoorbeeld = (invoer: AiInvoer) => api.post('/v1/professional/gespreksverslag/ai/voorbeeld', invoer).then(d<AiVoorbeeld>);
export const maakAiConcept = (invoer: AiInvoer) => api.post('/v1/professional/gespreksverslag/ai/concept', invoer).then(d<AiConcept>);

export const deel5Verplicht = (s: Sjabloon, volgnummer: number) => s !== 'C' && (volgnummer === 1 || volgnummer === 3 || volgnummer >= 5);

export type VerslagDetail =
  | { soort: 'legacy'; id: string; dossier_id: string | null; titel: string; datum: string; content: string; extended_data: Record<string, any> | null }
  | {
      soort: 'engine';
      id: string;
      dossier_id: string | null;
      titel: string;
      datum: string;
      sjabloon: Sjabloon;
      sjabloon_label: string;
      nieuw_incident: boolean;
      case_code: string | null;
      categorieen: string[];
      rolverhouding: string | null;
      geslacht_veroorzaker?: string | null;
      interventies: string[];
      doelen: string[];
      vak_a: string | null;
      vak_b: string | null;
      deel5: Deel5Weergave | null;
      toestemming: 'gegeven' | 'gevraagd' | 'verlopen' | null;
    };

export const getVerslag = (id: string) => api.get(`/v1/professional/gespreksverslag/${id}`).then(d<VerslagDetail>);

// ─── Medewerker ───────────────────────────────────────────────────────────────

export interface Deel5Weergave {
  arbo_kader: string;
  fte_belastbaarheid: number | null;
  verwachte_verzuimreductie: string | null;
  werkvloer_advies: string;
}

export interface MedewerkerVerslag {
  id: string;
  datum: string;
  gesprek: string;
  professional: string;
  persoonlijke_notities: string | null;
  deel5: Deel5Weergave | null;
  toestemming: 'gegeven' | 'gevraagd' | 'verlopen' | null;
}

export const getMijnVerslagen = () => api.get('/medewerker/gespreksverslagen').then(d<MedewerkerVerslag[]>);
export const geefToestemming = (id: string) => api.post(`/medewerker/gespreksverslagen/${id}/toestemming`).then(d<{ al_gegeven: boolean }>);

// ─── Werkgever ────────────────────────────────────────────────────────────────

export interface B2bUpdate extends Partial<Deel5Weergave> {
  id: string;
  maand: string;
  professional: string;
  sjabloon: string;
  gesprek: string;
  plek: string;
}

export interface Verlengingsverzoek {
  id: string;
  status: Exclude<VerlengingStatus, null>;
  extra_sessies: number;
  huidig_plafond: number;
  plek: string;
  professional: string;
  aangevraagd_op: string;
  besloten_op: string | null;
  notitie: string | null;
}

export const getB2bUpdates = () => api.get('/employer/gespreksverslag-engine/b2b-updates').then(d<B2bUpdate[]>);
export const getVerlengingen = () => api.get('/employer/gespreksverslag-engine/verlengingen').then(d<Verlengingsverzoek[]>);
export const besluitVerlenging = (id: string, akkoord: boolean, notitie?: string) =>
  api.post(`/employer/gespreksverslag-engine/verlengingen/${id}/besluit`, { akkoord, notitie: notitie || null }).then(d<{ status: string }>);
